// src/Pages/Shop/DepartmentStoreMap.js
import React, { useState, useEffect, useCallback } from "react";
import AnalogStick from "../Maps/logic/AnalogStick";
import DPad from "../Maps/logic/DPad";
import { mapConfigs } from "../Maps/MapConfig/mapConfigs";
import { getPlayerSpriteRowOffset } from "../Maps/logic/player";
import { clamp } from "../Maps/logic/helper";
import "./DepartmentStoreMap.css";

// Import reusable modal components
import DepartmentStoreModal from "../../components/Modals/DepartmentStoreModal";
import MiningStoreModal from "../../components/Modals/MiningStoreModal";
import TrainerStoreModal from "../../components/Modals/TrainerStoreModal";
import PotionTrainerModal from "../../components/Modals/PotionTrainerModal";
import TMTrainerModal from "../../components/Modals/TMTrainerModal";
import PromoModal from "../../components/Modals/PromoModal";
import ElevatorModal from "../../components/Modals/ElevatorModal";

// Import mining store constants
import { rockIcons } from "../Maps/MapConfig/rockIcons";
import { 
  pickaxeIcons, 
  sellPrices, 
  pickaxeDropRates, 
  pickaxeRequirements, 
  pickaxeDurability,
  getCatcherLevel
} from "../../constants/items";

import api from "../../api";

const DepartmentStoreMap = () => {
  // ── Control type (analog stick or D-pad) ──
  const [controlType, setControlType] = useState(
    localStorage.getItem("controlType") || "analog"
  );

  // Listen for control type changes
  useEffect(() => {
    const handleStorageChange = () => {
      const newControlType = localStorage.getItem("controlType") || "analog";
      setControlType(newControlType);
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(handleStorageChange, 1000);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // ── movement & map ─────────────────────────────────────
  const [currentFloor, setCurrentFloor] = useState("departmentStore1");
  const [playerPos, setPlayerPos]     = useState({ x: 100, y: 200 });
  const [direction, setDirection]     = useState("down");
  const [playerStep, setPlayerStep]   = useState(0);
  const [showCoordinates, setShowCoordinates] = useState(true); // Set to true to display coordinates

  const CONTROLS_ROW_HEIGHT_PX = 180;
  const [mapScale, setMapScale] = useState(1);
  const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  const {
    image: mapImageUrl,
    characterSprite: characterSpriteUrl,
    width: MAP_WIDTH,
    height: MAP_HEIGHT,
    collisionAreas,
    teleportZones,
    initialPlayer,
    npcs,
  } = mapConfigs[currentFloor];

  const isColliding = useCallback((x, y) => {
    // Check collision with walls and objects only
    // This allows player to move all the way to the edges
    const rect = { x, y, width: 32, height: 32 };
    return collisionAreas.some(area =>
      rect.x < area.x + area.width &&
      rect.x + rect.width > area.x &&
      rect.y < area.y + area.height &&
      rect.y + rect.height > area.y
    );
  }, [collisionAreas]);

  const checkTeleport = useCallback((x, y) =>
    teleportZones?.find(zone =>
      x < zone.x + zone.width &&
      x + 32 > zone.x &&
      y < zone.y + zone.height &&
      y + 32 > zone.y
    ),
  [teleportZones]);

  const switchFloor = useCallback((floor, tx, ty) => {
    setCurrentFloor(floor);
    setPlayerPos({ x: tx, y: ty });
    setDirection("down");
    setPlayerStep(0);
  }, []);

  // D-Pad handler with throttling (keyboard-style movement)
  const lastDPadMoveRef = React.useRef(0);
  const handleDPad = useCallback(({ x, y }) => {
    // Throttle D-pad input to prevent too-fast movement
    const now = Date.now();
    if (now - lastDPadMoveRef.current < 150) {
      return; // Ignore if less than 150ms since last move
    }
    
    const moving = Math.abs(x) >= 0.1 || Math.abs(y) >= 0.1;
    if (!moving) return;

    lastDPadMoveRef.current = now;

    let nx = playerPos.x;
    let ny = playerPos.y;
    let dir = direction;

    // Move by 4 pixels like keyboard
    if (Math.abs(x) > Math.abs(y)) {
      dir = x > 0 ? "right" : "left";
      nx += (x > 0 ? 1 : -1) * 4;
    } else {
      dir = y > 0 ? "down" : "up";
      ny += (y > 0 ? 1 : -1) * 4;
    }

    if (!isColliding(nx, ny)) {
      const tp = checkTeleport(nx, ny);
      if (tp) {
        switchFloor(tp.targetFloor, tp.targetX, tp.targetY);
        return;
      }
      setPlayerPos({ x: nx, y: ny });
      setDirection(dir);
      setPlayerStep(prev => (prev + 1) % 3);
    }
  }, [playerPos, direction, isColliding, checkTeleport, switchFloor]);

  // Analog stick handler (smooth movement)
  const handleStick = useCallback(({ x, y }) => {
    const TH = 0.1, speed = 1;
    if (Math.abs(x) < TH && Math.abs(y) < TH) return;
    const nx = playerPos.x + x * speed;
    const ny = playerPos.y + y * speed;
    const dir = Math.abs(x) > Math.abs(y)
      ? (x > 0 ? "right" : "left")
      : (y > 0 ? "down" : "up");
    if (!isColliding(nx, ny)) {
      const tp = checkTeleport(nx, ny);
      if (tp) {
        switchFloor(tp.targetFloor, tp.targetX, tp.targetY);
        return;
      }
      setPlayerPos({ x: nx, y: ny });
      setDirection(dir);
      setPlayerStep(prev => (prev + 1) % 3);
    }
  }, [playerPos, isColliding, checkTeleport, switchFloor]);

  useEffect(() => {
    const keyMap = {
      ArrowUp: [0, -4, "up"],    w: [0,-4,"up"],
      ArrowDown: [0, 4, "down"], s: [0,4,"down"],
      ArrowLeft: [-4,0,"left"],   a: [-4,0,"left"],
      ArrowRight: [4,0,"right"],  d: [4,0,"right"],
    };
    const onKeyDown = e => {
      // Toggle coordinates display with C key
      if (e.key === 'c') {
        setShowCoordinates(prev => !prev);
        return;
      }
      
      const k = keyMap[e.key];
      if (!k) return;
      e.preventDefault();
      const [dx, dy, nd] = k;
      const nx = playerPos.x + dx, ny = playerPos.y + dy;
      if (!isColliding(nx, ny)) {
        const tp = checkTeleport(nx, ny);
        if (tp) {
          switchFloor(tp.targetFloor, tp.targetX, tp.targetY);
          return;
        }
        setPlayerPos({ x: nx, y: ny });
        setDirection(nd);
        if (e.repeat) setPlayerStep(prev => (prev + 1) % 3);
        else [1,2,0].forEach((s,i)=>
          setTimeout(() => setPlayerStep(s), i*150)
        );
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [playerPos, isColliding, checkTeleport, switchFloor]);

  // ── department store & currency ───────────────────────────────────
  const [storeItems, setStoreItems]   = useState([]);
  const [currency, setCurrency]       = useState({ money:0, coins:0 });
  const [quantityMap, setQuantityMap] = useState({});
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [catcherExp, setCatcherExp] = useState(0);
  const [catcherLevel, setCatcherLevel] = useState(0);
  
  // Item categories
  const categories = [
    { id: 'all', name: 'All Items' },
    { id: 'balls', name: 'Poké Balls' },
    { id: 'misc', name: 'Miscellaneous' }
  ];
  
  // Function to categorize items
  const categorizeItem = (item) => {
    const name = item.name.toLowerCase();
    
    if (name.includes('ball')) return 'balls';
    if (name.includes('potion') || name.includes('heal') || name.includes('revive') || 
        name.includes('medicine') || name.includes('cure')) return 'medicine';
    if (name.includes('berry')) return 'berries';
    if (name.includes('tm') || name.includes('hm')) return 'tm';
    if (name.includes('key')) return 'key';
    if (name.includes('x-') || name.includes('battle') || name.includes('boost')) return 'battle';
    
    return 'misc';
  };
  
  // Filter items by category
  const filteredItems = activeCategory === 'all' 
    ? storeItems 
    : storeItems.filter(item => categorizeItem(item) === activeCategory);

  // ── mining store state ───────────────────────────────────
  const [miningModalOpen, setMiningModalOpen] = useState(false);
  const [miningMode, setMiningMode] = useState(null); // null, 'buy', or 'sell'
  const [miningActiveTab, setMiningActiveTab] = useState('pickaxe');
  const [miningItems, setMiningItems] = useState([]);
  const [miningInfo, setMiningInfo] = useState(null);
  const [loadingMiningInfo, setLoadingMiningInfo] = useState(true);
  const [buyAmounts, setBuyAmounts] = useState({});
  const [sellAmounts, setSellAmounts] = useState({});

  // ── trainer store state ───────────────────────────────────
  const [trainerModalOpen, setTrainerModalOpen] = useState(false);
  const [trainerItems, setTrainerItems] = useState([]);
  const [trainerInfo, setTrainerInfo] = useState(null);
  const [loadingTrainerInfo, setLoadingTrainerInfo] = useState(true);

  // ── TM trainer modal state ───────────────────────────────────
  const [tmTrainerModalOpen, setTmTrainerModalOpen] = useState(false);
  const [trainerBuyAmounts, setTrainerBuyAmounts] = useState({});

  // ── potion trainer store state ───────────────────────────────────
  const [potionTrainerModalOpen, setPotionTrainerModalOpen] = useState(false);
  const [potionTrainerItems, setPotionTrainerItems] = useState([]);
  const [potionTrainerInfo, setPotionTrainerInfo] = useState(null);
  const [loadingPotionTrainerInfo, setLoadingPotionTrainerInfo] = useState(true);
  const [potionTrainerBuyAmounts, setPotionTrainerBuyAmounts] = useState({});

  // ── elevator state ───────────────────────────────────
  const [elevatorModalOpen, setElevatorModalOpen] = useState(false);



  // ── floating text system (similar to GameMap) ─────────
  const [floatingTexts, setFloatingTexts] = useState([]);

  const spawnText = (text, x, y, color = "#ffe066", imageUrl = null) => {
    const id = Date.now() + Math.random();
    setFloatingTexts((ft) => [
      ...ft,
      { id, text, x, y, color, imageUrl, created: Date.now() },
    ]);
    setTimeout(() => {
      setFloatingTexts((ft) => ft.filter((f) => f.id !== id));
    }, 2500);
  };

  const fetchStore = useCallback(async () => {
    try {
      // Fetch store items, currency, and menu details for catcher experience
      const [storeRes, currRes, menuDetailsRes] = await Promise.all([
        api.get("/v1/department-store", { withCredentials: true }),
        api.get("/v1/currency", { withCredentials: true }),
        api.get("/v1/menu/details", { withCredentials: true })
      ]);
      setStoreItems(storeRes.data);
      setCurrency(Array.isArray(currRes.data) ? currRes.data[0] : currRes.data);
      
      // Get catcher experience and calculate level
      if (menuDetailsRes.data && menuDetailsRes.data.catcher_exp) {
        const exp = menuDetailsRes.data.catcher_exp;
        const level = getCatcherLevel(exp);
        console.log('Catcher exp:', exp, 'Calculated level:', level);
        setCatcherExp(exp);
        setCatcherLevel(level);
      }
    } catch (err) {
      console.error("Error loading store:", err);
    }
  }, []);

  useEffect(() => { fetchStore() }, [fetchStore]);
  
  // Mining store handlers
  const fetchMiningStore = useCallback(async () => {
    setLoadingMiningInfo(true);
    try {
      const [miningStoreRes, currencyRes, miningInfoRes] = await Promise.all([
        api.get("/v1/mining-store", { withCredentials: true }),
        api.get("/v1/currency", { withCredentials: true }),
        api.get("/v1/mining/info", { withCredentials: true })
      ]);
      
      // Accept either direct array or wrapped response
      const itemsData = Array.isArray(miningStoreRes.data) ? miningStoreRes.data : (miningStoreRes.data.items || []);
      setMiningItems(itemsData);
      setCurrency(Array.isArray(currencyRes.data) ? currencyRes.data[0] : currencyRes.data);
      setMiningInfo(miningInfoRes.data);
    } catch (err) {
      console.error("Error loading mining store:", err);
      spawnText("Failed to load mining store", playerPos.x, playerPos.y - 20, "#f44336");
    } finally {
      setLoadingMiningInfo(false);
    }
  }, [playerPos.x, playerPos.y]);
  
  const toggleMiningModal = () => {
    if (!miningModalOpen) {
      setMiningMode('buy'); // Default to buy screen
      fetchMiningStore();
    }
    setMiningModalOpen(o => !o);
  };
  
  const setMiningModeAndFetch = (mode) => {
    setMiningMode(mode);
    fetchMiningStore();
  };
  
  const handleMiningQuantityChange = (id, value, isBuy = true) => {
    if (isBuy) {
      setBuyAmounts(prev => ({ ...prev, [id]: parseInt(value, 10) || 1 }));
    } else {
      setSellAmounts(prev => ({ ...prev, [id]: parseInt(value, 10) || 1 }));
    }
  };
  
  const handleMiningPurchase = async (item) => {
    const quantity = buyAmounts[item.id] || 1;
    try {
      await api.post(
        "/v1/purchase",
        { item: item.id, quantity },
        { withCredentials: true }
      );
      
      // Close modal immediately
      setMiningModalOpen(false);
      
      // Show floating text for item gained
      const itemIcon = item.image ? `/${item.image}` : (item.name.toLowerCase().includes('pickaxe') ? (pickaxeIcons[item.id] || `/Items/${item.id}.png`) : `/Items/${item.id}.png`);
      spawnText(
        `+${quantity}× ${item.name}`,
        playerPos.x,
        playerPos.y - 20,
        "#4CAF50",
        itemIcon
      );
      
      // Show floating text for money/coins spent (in red)
      setTimeout(() => {
        const totalMoney = (item.money || 0) * quantity;
        const totalCoins = (item.coins || 0) * quantity;
        
        if (totalMoney > 0) {
          spawnText(
            `-$${totalMoney.toLocaleString()}`,
            playerPos.x,
            playerPos.y - 40,
            "#f44336"
          );
        }
        if (totalCoins > 0) {
          spawnText(
            `-${totalCoins.toLocaleString()} Coins`,
            playerPos.x,
            playerPos.y - (totalMoney > 0 ? 60 : 40),
            "#f44336"
          );
        }
      }, 300);
      
      // Refresh store data
      fetchMiningStore();
      
    } catch (err) {
      const detail = err.response?.data?.detail || "Purchase failed.";
      spawnText(detail, playerPos.x, playerPos.y - 20, "#f44336");
    }
  };
  
  const handleMiningSell = async (item) => {
    const quantity = sellAmounts[item.id] || 1;
    try {
      const res = await api.post(
        "/v1/sell",
        { item: item.id, quantity },
        { withCredentials: true }
      );
      
      // Close modal immediately
      setMiningModalOpen(false);
      
      // Show floating text for item sold
      const itemIcon = item.image ? `/${item.image}` : (item.name.toLowerCase().includes('pickaxe') ? (pickaxeIcons[item.id] || `/Items/${item.id}.png`) : `/Items/${item.id}.png`);
      spawnText(
        `-${quantity}× ${item.name}`,
        playerPos.x,
        playerPos.y - 20,
        "#f44336",
        itemIcon
      );
      
      // Show floating text for money gained (in green)
      const coinsGained = res.data.coinsGained || quantity * (sellPrices[item.id] || 0);
      setTimeout(() => {
        spawnText(
          `+$${coinsGained.toLocaleString()}`,
          playerPos.x,
          playerPos.y - 40,
          "#4CAF50"
        );
      }, 300);
      
      // Refresh store data
      fetchMiningStore();
      
    } catch (err) {
      const detail = err.response?.data?.detail || "Sale failed.";
      spawnText(detail, playerPos.x, playerPos.y - 20, "#f44336");
    }
  };

  // ── elevator functions ───────────────────────────────────
  const toggleElevatorModal = () => {
    setElevatorModalOpen(o => !o);
  };

  const handleFloorSelect = (targetFloor) => {
    const floorMap = {
      1: 'departmentStore1',
      2: 'departmentStore2', 
      3: 'departmentStore3'
    };
    
    const targetFloorKey = floorMap[targetFloor];
    if (targetFloorKey && targetFloorKey !== currentFloor) {
      // Position player in front of elevator NPC on target floor
      const elevatorPositions = {
        1: { x: 130, y: 70 },
        2: { x: 130, y: 70 },
        3: { x: 130, y: 70 }
      };
      
      const pos = elevatorPositions[targetFloor];
      switchFloor(targetFloorKey, pos.x, pos.y);
      
      // Show floating text for floor change
      spawnText(
        `Elevator to Floor ${targetFloor}`,
        playerPos.x,
        playerPos.y - 20,
        "#3498db"
      );
    }
  };

  const getCurrentFloorNumber = () => {
    const floorNumbers = {
      'departmentStore1': 1,
      'departmentStore2': 2,
      'departmentStore3': 3
    };
    return floorNumbers[currentFloor] || 1;
  };
  
  // Initialize player position only on first load, not when changing floors
  useEffect(() => {
    // Only set initial position on first component mount
    if (initialPlayer && currentFloor === "departmentStore1") {
      setPlayerPos({ x: initialPlayer.x, y: initialPlayer.y });
      setDirection(initialPlayer.direction);
      setPlayerStep(initialPlayer.step);
    }
  }, [initialPlayer, currentFloor, setPlayerPos, setDirection, setPlayerStep]);

  const toggleStoreModal = () => {
    if (!storeModalOpen) fetchStore();
    setStoreModalOpen(o=>!o);
  };

  const handleQuantityChange = (id,v) =>
    setQuantityMap(q=>({...q,[id]:v}));

  const handlePurchase = async item => {
    const qty = parseInt(quantityMap[item.id]||1,10);
    const totalMoney = (item.money || 0) * qty;
    const totalCoins = (item.coins || 0) * qty;
    
    try {
      await api.post(
        "/v1/purchase",
        { item: item.id, quantity: qty },
        { withCredentials: true }
      );
      
      // Close modal immediately
      setStoreModalOpen(false);
      
      // Show floating text for item gained
      spawnText(
        `+${qty}× ${item.name}`,
        playerPos.x,
        playerPos.y - 20,
        "#4CAF50",
        `/${item.image}`
      );
      
      // Show floating text for money/coins spent (in red)
      setTimeout(() => {
        if (totalMoney > 0) {
          spawnText(
            `-$${totalMoney.toLocaleString()}`,
            playerPos.x,
            playerPos.y - 40,
            "#f44336"
          );
        }
        if (totalCoins > 0) {
          spawnText(
            `-${totalCoins.toLocaleString()} Coins`,
            playerPos.x,
            playerPos.y - (totalMoney > 0 ? 60 : 40),
            "#f44336"
          );
        }
      }, 300);
      
      // Refresh store data
      fetchStore();
      
    } catch (err) {
      const detail = err.response?.data?.detail || "Purchase failed.";
      spawnText(detail, playerPos.x, playerPos.y - 20, "#f44336");
    }
  };

  // ── trainer store functions ───────────────────────────────────
  const fetchTrainerStore = useCallback(async () => {
    try {
      setLoadingTrainerInfo(true);
      // Call the trainer arsenal API endpoint and get both currency and mining info
      const [trainerRes, currRes, miningRes] = await Promise.all([
        api.get("/v1/trainer-arsenal", { withCredentials: true }),
        api.get("/v1/currency", { withCredentials: true }),
        api.get("/v1/mining/info", { withCredentials: true })
      ]);
      
      setTrainerItems(trainerRes.data);
      
      // Merge currency data with mining resources (gems)
      const currencyData = Array.isArray(currRes.data) ? currRes.data[0] : currRes.data;
      const miningData = miningRes.data;
      
      const mergedCurrency = {
        ...currencyData,
        // Override gem counts with mining resource data
        ruby: miningData.resources?.ruby || 0,
        sapphire: miningData.resources?.sapphire || 0,
        emerald: miningData.resources?.emerald || 0,
        gold: miningData.resources?.gold || 0,
        diamond: miningData.resources?.diamond || 0
      };
      
      setCurrency(mergedCurrency);
      setTrainerInfo({ trainer_type: "ev" });
      
    } catch (err) {
      console.error("Error loading trainer store:", err);
    } finally {
      setLoadingTrainerInfo(false);
    }
  }, []);

  const toggleTrainerModal = () => {
    if (!trainerModalOpen) fetchTrainerStore();
    setTrainerModalOpen(o => !o);
  };

  const handleTrainerQuantityChange = (id, v) => {
    const val = parseInt(v, 10) || 1;
    setTrainerBuyAmounts(q => ({ ...q, [id]: Math.max(1, Math.min(99, val)) }));
  };

  const handleTrainerPurchase = async (item) => {
    const qty = parseInt(trainerBuyAmounts[item.id] || 1, 10);
    const currencyKeys = ['money', 'coins', 'ruby', 'sapphire', 'emerald', 'gold', 'diamond'];
    
    try {
      await api.post(
        "/v1/purchase",
        { item: item.id, quantity: qty },
        { withCredentials: true }
      );
      
      // Close modal immediately
      setTrainerModalOpen(false);
      
      // Show floating text for item gained
      spawnText(
        `+${qty}× ${item.name}`,
        playerPos.x,
        playerPos.y - 20,
        "#4CAF50",
        `/${item.image}`
      );
      
      // Show floating text for currencies spent (in red)
      let yOffset = 40;
      
      // Handle 'other' field (battle coins) first
      const otherCost = (item.other || 0) * qty;
      if (otherCost > 0) {
        setTimeout(() => {
          spawnText(
            `-${otherCost.toLocaleString()} Battle Coins`,
            playerPos.x,
            playerPos.y - yOffset,
            "#f44336"
          );
          yOffset += 20;
        }, 100);
      }
      
      // Handle other currencies
      currencyKeys.forEach((key, index) => {
        const cost = (item[key] || 0) * qty;
        if (cost > 0) {
          setTimeout(() => {
            const displayText = key === 'money' ? `-$${cost.toLocaleString()}` : 
                               `-${cost.toLocaleString()} ${key.charAt(0).toUpperCase() + key.slice(1)}`;
            spawnText(
              displayText,
              playerPos.x,
              playerPos.y - yOffset,
              "#f44336"
            );
            yOffset += 20;
          }, (index + 2) * 200);
        }
      });
      
      // Refresh store data
      fetchTrainerStore();
      
    } catch (err) {
      const detail = err.response?.data?.detail || "Purchase failed.";
      spawnText(detail, playerPos.x, playerPos.y - 20, "#f44336");
    }
  };

  // ── potion trainer store functions ───────────────────────────────────
  const fetchPotionTrainerStore = useCallback(async () => {
    try {
      setLoadingPotionTrainerInfo(true);
      // Call the trainer arsenal API endpoint and get both currency and mining info
      const [trainerRes, currRes, miningRes] = await Promise.all([
        api.get("/v1/trainer-arsenal", { withCredentials: true }),
        api.get("/v1/currency", { withCredentials: true }),
        api.get("/v1/mining/info", { withCredentials: true })
      ]);
      
      setPotionTrainerItems(trainerRes.data);
      
      // Merge currency data with mining resources (gems)
      const currencyData = Array.isArray(currRes.data) ? currRes.data[0] : currRes.data;
      const miningData = miningRes.data;
      
      const mergedCurrency = {
        ...currencyData,
        // Override gem counts with mining resource data
        ruby: miningData.resources?.ruby || 0,
        sapphire: miningData.resources?.sapphire || 0,
        emerald: miningData.resources?.emerald || 0,
        gold: miningData.resources?.gold || 0,
        diamond: miningData.resources?.diamond || 0
      };
      
      setCurrency(mergedCurrency);
      setPotionTrainerInfo({ trainer_type: "potion" });
      
    } catch (err) {
      console.error("Error loading potion trainer store:", err);
    } finally {
      setLoadingPotionTrainerInfo(false);
    }
  }, []);

  const togglePotionTrainerModal = () => {
    if (!potionTrainerModalOpen) fetchPotionTrainerStore();
    setPotionTrainerModalOpen(o => !o);
  };

  const handlePotionTrainerQuantityChange = (id, v) => {
    const val = parseInt(v, 10) || 1;
    setPotionTrainerBuyAmounts(q => ({ ...q, [id]: Math.max(1, Math.min(99, val)) }));
  };

  const handlePotionTrainerPurchase = async (item) => {
    const qty = parseInt(potionTrainerBuyAmounts[item.id] || 1, 10);
    const currencyKeys = ['money', 'coins', 'ruby', 'sapphire', 'emerald', 'gold', 'diamond'];
    
    try {
      await api.post(
        "/v1/purchase",
        { item: item.id, quantity: qty },
        { withCredentials: true }
      );
      
      // Close modal immediately
      setPotionTrainerModalOpen(false);
      
      // Show floating text for item gained
      spawnText(
        `+${qty}× ${item.name}`,
        playerPos.x,
        playerPos.y - 20,
        "#4CAF50",
        `/${item.image}`
      );
      
      // Show floating text for currencies spent (in red)
      let yOffset = 40;
      
      // Handle 'other' field (battle coins) first
      const otherCost = (item.other || 0) * qty;
      if (otherCost > 0) {
        setTimeout(() => {
          spawnText(
            `-${otherCost.toLocaleString()} Battle Coins`,
            playerPos.x,
            playerPos.y - yOffset,
            "#f44336"
          );
          yOffset += 20;
        }, 100);
      }
      
      // Handle other currencies
      currencyKeys.forEach((key, index) => {
        const cost = (item[key] || 0) * qty;
        if (cost > 0) {
          setTimeout(() => {
            const displayText = key === 'money' ? `-$${cost.toLocaleString()}` : 
                               `-${cost.toLocaleString()} ${key.charAt(0).toUpperCase() + key.slice(1)}`;
            spawnText(
              displayText,
              playerPos.x,
              playerPos.y - yOffset,
              "#f44336"
            );
            yOffset += 20;
          }, (index + 2) * 200);
        }
      });
      
      // Refresh store data
      fetchPotionTrainerStore();
      
    } catch (err) {
      const detail = err.response?.data?.detail || "Purchase failed.";
      spawnText(detail, playerPos.x, playerPos.y - 20, "#f44336");
    }
  };

  // ── camera ─────────────────────────────────────────────
  // Responsive viewport sizing for mobile devices
  const getResponsiveViewport = () => {
    const isMobile = window.innerWidth <= 480;
    if (isMobile) {
      // Use smaller viewport for mobile to ensure proper display
      const mobileWidth = Math.min(window.innerWidth - 40, 300);
      const mobileHeight = Math.min(window.innerHeight * 0.6, 200);
      return { width: mobileWidth, height: mobileHeight };
    }
    // Use config dimensions for desktop
    return {
      width: mapConfigs[currentFloor].viewport?.width || 336,
      height: mapConfigs[currentFloor].viewport?.height || 240
    };
  };
  
  const { width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT } = getResponsiveViewport();

  useEffect(() => {
    function updateScale() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setViewportSize({ width: w, height: h });

      const availableWidth = w;
      const availableHeight = Math.max(0, h - CONTROLS_ROW_HEIGHT_PX);

      const scaleX = availableWidth / VIEWPORT_WIDTH;
      const scaleY = availableHeight / VIEWPORT_HEIGHT;
      const scale = Math.min(scaleX, scaleY);

      setMapScale(clamp(scale, 1, 4));
    }

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [VIEWPORT_WIDTH, VIEWPORT_HEIGHT]);
  
  // Calculate camera position to follow player - exact same as GameMap
  const camX = clamp(playerPos.x - VIEWPORT_WIDTH / 2, 0, MAP_WIDTH - VIEWPORT_WIDTH);
  const camY = clamp(playerPos.y - VIEWPORT_HEIGHT / 2, 0, MAP_HEIGHT - VIEWPORT_HEIGHT);

  const scaledViewportWidth = VIEWPORT_WIDTH * mapScale;
  const scaledViewportHeight = VIEWPORT_HEIGHT * mapScale;
  const mapShellWidth = Math.min(scaledViewportWidth, viewportSize.width);
  const mapShellHeight = Math.min(scaledViewportHeight, Math.max(0, viewportSize.height - CONTROLS_ROW_HEIGHT_PX));

  const controlsDisabled =
    storeModalOpen ||
    miningModalOpen ||
    trainerModalOpen ||
    potionTrainerModalOpen ||
    tmTrainerModalOpen ||
    elevatorModalOpen;

  return (
    <div className="department-store-map">
      <div className="layout-container">
        <div className="map-section">
          <div className="map-wrapper">
            <div className="map-scale-shell" style={{ width: mapShellWidth, height: mapShellHeight, overflow: 'hidden' }}>
              <div className="map-scale-wrapper" style={{ '--map-scale': mapScale }}>
            <div
              className="game-container"
              style={{
                width: VIEWPORT_WIDTH,
                height: VIEWPORT_HEIGHT,
                overflow: 'hidden'
              }}
            >
              <div
                className="map"
                style={{
                  backgroundImage: `url(${mapImageUrl})`,
                  backgroundPosition: `-${camX}px -${camY}px`,
                  width: VIEWPORT_WIDTH,
                  height: VIEWPORT_HEIGHT,
                  backgroundSize: `${MAP_WIDTH}px ${MAP_HEIGHT}px`,
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: '#000'
                }}
              >
                <div
                  className="character"
                  style={{
                    left:  playerPos.x - camX,
                    top:   playerPos.y - camY,
                    backgroundImage: `url(${characterSpriteUrl})`,
                    backgroundPosition: `-${playerStep*32}px -${getPlayerSpriteRowOffset(direction)}px`
                  }}
                />
                
                {/* Floating text overlay */}
                {floatingTexts.map((ft) => (
                  <div
                    key={ft.id}
                    className="floating-text"
                    style={{
                      position: "absolute",
                      left: ft.x - camX,
                      top: ft.y - camY,
                      color: ft.color,
                      fontSize: "12px",
                      fontWeight: "bold",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
                      pointerEvents: "none",
                      zIndex: 1000,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      animation: "floatUp 2.5s ease-out forwards"
                    }}
                  >
                    {ft.imageUrl && (
                      <img
                        src={ft.imageUrl}
                        alt=""
                        style={{
                          width: "16px",
                          height: "16px",
                          imageRendering: "pixelated"
                        }}
                      />
                    )}
                    {ft.text}
                  </div>
                ))}
                {npcs && npcs.map(npc=>(
                  <div
                    key={npc.id}
                    className="trainer"
                    onClick={npc.type === 'mining' ? toggleMiningModal : 
                             npc.type === 'trainer-ev' ? toggleTrainerModal : 
                             npc.type === 'trainer-potion' ? togglePotionTrainerModal : 
                             npc.type === 'tm-trainer' ? () => setTmTrainerModalOpen(true) :
                             npc.type === 'elevator' ? toggleElevatorModal :
                             toggleStoreModal}
                    style={{
                      position:'absolute',
                      left: npc.x - camX,
                      top:  npc.y - camY,
                      width: npc.width,
                      height: npc.height,
                      backgroundImage: `url(${npc.sprite})`,
                      backgroundPosition: `-${npc.step * 32}px -${npc.direction * 32}px`,
                      cursor:'pointer',
                      zIndex: 100
                    }}
                  />
                ))}
                
                {/* collision areas
                {collisionAreas && collisionAreas.map((area, index) => (
                  <div
                    key={`collision-${index}`}
                    className="collision-area"
                    style={{
                      position: 'absolute',
                      left: area.x - camX,
                      top: area.y - camY,
                      width: area.width,
                      height: area.height,
                    }}
                  />
                ))}
        */}
              </div>

              {/* show coordinates 
              {showCoordinates && (
                <div className="coordinates-display">
                  X: {Math.round(playerPos.x)}, Y: {Math.round(playerPos.y)}
                </div>
              )}
           */}
            </div>
              </div>
            </div>
          </div>

          {!controlsDisabled && (
            <div className="movement-controls-row">
              <div className="analog-stick-wrapper">
                {controlType === 'analog' ? (
                  <AnalogStick onMove={handleStick} />
                ) : (
                  <DPad onMove={handleDPad} />
                )}
              </div>
            </div>
          )}

          {/* Department Store Modal using reusable component */}
          <DepartmentStoreModal
            isOpen={storeModalOpen}
            toggle={toggleStoreModal}
            storeItems={storeItems}
            currency={currency}
            categories={categories}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            filteredItems={filteredItems}
            buyAmounts={quantityMap}
            handleQuantityChange={handleQuantityChange}
            handlePurchase={handlePurchase}
            loadingItems={storeItems.length === 0}
            catcherExp={catcherExp}
            catcherLevel={catcherLevel}
          />
          
          {/* Mining Store Modal using reusable component */}
          <MiningStoreModal
            isOpen={miningModalOpen}
            toggle={toggleMiningModal}
            miningMode={miningMode}
            setMiningMode={setMiningModeAndFetch}
            miningActiveTab={miningActiveTab}
            setMiningActiveTab={setMiningActiveTab}
            miningItems={miningItems}
            miningInfo={miningInfo}
            loadingMiningInfo={loadingMiningInfo}
            currency={currency}
            buyAmounts={buyAmounts}
            sellAmounts={sellAmounts}
            handleMiningQuantityChange={handleMiningQuantityChange}
            handleMiningPurchase={handleMiningPurchase}
            handleMiningSell={handleMiningSell}
            pickaxeRequirements={pickaxeRequirements}
            pickaxeDropRates={pickaxeDropRates}
            pickaxeDurability={pickaxeDurability}
            sellPrices={sellPrices}
            rockIcons={rockIcons}
            pickaxeIcons={pickaxeIcons}
          />
          
          {/* Trainer Store Modal using reusable component */}
          <TrainerStoreModal
            isOpen={trainerModalOpen}
            toggle={toggleTrainerModal}
            trainerItems={trainerItems}
            trainerInfo={trainerInfo}
            loadingTrainerInfo={loadingTrainerInfo}
            currency={currency}
            buyAmounts={trainerBuyAmounts}
            handleTrainerQuantityChange={handleTrainerQuantityChange}
            handleTrainerPurchase={handleTrainerPurchase}
            trainerType="trainer-ev"
          />
          
          {/* Potion Trainer Store Modal using reusable component */}
          <PotionTrainerModal
            isOpen={potionTrainerModalOpen}
            toggle={togglePotionTrainerModal}
            trainerItems={potionTrainerItems}
            trainerInfo={potionTrainerInfo}
            loadingTrainerInfo={loadingPotionTrainerInfo}
            currency={currency}
            buyAmounts={potionTrainerBuyAmounts}
            handleTrainerQuantityChange={handlePotionTrainerQuantityChange}
            handleTrainerPurchase={handlePotionTrainerPurchase}
            trainerType="trainer-potion"
          />
          
          {/* TM Trainer Modal */}
          <TMTrainerModal
            isOpen={tmTrainerModalOpen}
            toggle={() => setTmTrainerModalOpen(false)}
            spawnText={spawnText}
            playerPos={playerPos}
          />

          {/* Elevator Modal */}
          <ElevatorModal
            isOpen={elevatorModalOpen}
            toggle={toggleElevatorModal}
            currentFloor={getCurrentFloorNumber()}
            onFloorSelect={handleFloorSelect}
            availableFloors={[1, 2, 3]}
          />

        </div>
      </div>
    </div>
  );
};

export default DepartmentStoreMap;
